/* eslint-disable no-nested-ternary */
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // <-- Imported useNavigate

// Icons
import CloseIcon from '@mui/icons-material/Close';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import axiosInstance from 'src/api/axiosInstance';

// --- Shared Helpers ---
const token = sessionStorage.getItem('token');

const downloadHelpDocument = async () => {
  try {
    const { data } = await axiosInstance.get('/rest/help/download', {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (data.error) {
      console.error(data.error);
      return;
    }

    window.open(data.downloadUrl, '_blank', 'noopener');
  } catch (error) {
    console.error('Failed to download help document:', error);
  }
};

// --- Extracted Card Component ---
const SupportActionCard = ({
  icon,
  title,
  subtitle,
  onClick,
  gradient,
  color,
  featured,
  loading,
  external,
}) => (
  <Box
    onClick={loading ? undefined : onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      mb: 1.25,
      borderRadius: '14px',
      bgcolor: featured ? '#F0FDF4' : '#FFFFFF',
      border: '1px solid',
      borderColor: featured ? '#86EFAC' : '#E5E7EB',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: featured ? '0 4px 16px rgba(16, 185, 129, 0.08)' : '0 2px 6px rgba(0,0,0,0.02)',
      '&:hover': {
        transform: loading ? 'none' : 'translateY(-2px)',
        borderColor: featured ? '#22C55E' : color,
        boxShadow: featured
          ? '0 6px 20px rgba(16, 185, 129, 0.15)'
          : `0 6px 16px ${color}15`,
        '& .action-icon': {
          transform: 'translateX(4px)',
          color,
        },
      },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 4px 10px ${color}25`,
      }}
    >
      {icon}
    </Box>

    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontWeight: 700, color: featured ? '#065F46' : '#1F2937', fontSize: '0.875rem' }}>
        {title}
      </Typography>
      <Typography sx={{ color: '#6B7280', fontSize: '0.75rem', mt: 0.25 }}>
        {subtitle}
      </Typography>
    </Box>

    {loading ? (
      <CircularProgress size={18} sx={{ color, mr: 1 }} />
    ) : external ? (
      <OpenInNewRoundedIcon className="action-icon" sx={{ color: '#9CA3AF', transition: 'all 0.3s ease', fontSize: '1.1rem' }} />
    ) : (
      <ChevronRightRoundedIcon className="action-icon" sx={{ color: '#9CA3AF', transition: 'all 0.3s ease', fontSize: '1.25rem' }} />
    )}
  </Box>
);

// Prop validations
SupportActionCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  gradient: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  featured: PropTypes.bool,
  loading: PropTypes.bool,
  external: PropTypes.bool,
};

SupportActionCard.defaultProps = {
  featured: false,
  loading: false,
  external: false,
};

// --- Main Dialog Component ---
export default function HelpDialog({ open, onClose }) {
  const navigate = useNavigate(); // <-- Initialize navigate hook
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const fetchProvider = async () => {
        setLoading(true);
        try {
          const userToken = sessionStorage.getItem('token');
          const response = await axiosInstance.get('/rest/preferences/email-provider-name', {
            headers: { Authorization: `Bearer ${userToken}` },
          });
          setProvider(response.data);
        } catch (error) {
          console.error('Failed to fetch email provider name:', error);
          setProvider('OTHER');
        } finally {
          setLoading(false);
        }
      };

      fetchProvider();
    } else {
      setProvider(null);
      setLoading(false);
    }
  }, [open]);

  const handleCreateTicket = () => {
    navigate('/dashboard/support/tickets/new'); // <-- Swapped to react-router navigation
    onClose();
  };

  const handleEmailSupportClick = () => {
    if (loading) return;

    const email = 'support@merudesk.com';
    const subject = 'Support Request';
    const encodedSubject = encodeURIComponent(subject);
    let url = '';

    const providerUpper = provider ? provider.toUpperCase() : '';

    if (providerUpper === 'GOOGLE') {
      url = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodedSubject}`;
    } else if (providerUpper === 'ZOHO') {
      url = `https://mail.zoho.com/zm/#compose?to=${email}&subject=${encodedSubject}`;
    } else {
      url = `https://outlook.office.com/mail/deeplink/compose?to=${email}&subject=${encodedSubject}`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '380px', 
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.15)',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(15, 23, 42, 0.4)', 
        },
      }}
    >
      {/* ── Premium Header Banner ── */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0a5c37 0%, #0E9F60 50%, #2dd4a0 100%)',
          pt: 3.5, 
          pb: 3, 
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          overflow: 'hidden',
          flexShrink: 0, 
        }}
      >
        {/* Floating Glassmorphism Circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0))',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -20,
            width: 90,
            height: 90,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0))',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}
        />

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(4px)',
            width: 28,
            height: 28,
            transition: 'all 0.2s',
            zIndex: 2,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.3)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '1rem' }} />
        </IconButton>

        {/* Hero Icon */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 52, 
            height: 52,
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(12px)',
            border: '1.5px solid rgba(255,255,255,0.3)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            mb: 1.5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" /><path d="M21 16v2a4 4 0 0 1-4 4h-5" /></svg>
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff', letterSpacing: '0.2px', position: 'relative', zIndex: 1, lineHeight: 1.2 }}>
          How can we help?
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem', mt: 0.75, position: 'relative', zIndex: 1, lineHeight: 1.4 }}>
          Choose an option below to get assistance.
        </Typography>
      </Box>

      {/* ── Body Content ── */}
      <DialogContent sx={{ px: 2.5, py: 3, bgcolor: '#FAFAFA' }}>
        
        <SupportActionCard
          featured
          title="Create a Support Ticket"
          subtitle="Best for tracking complex issues"
          gradient="linear-gradient(135deg, #0E9F60 0%, #059669 100%)"
          color="#10B981"
          icon={<ConfirmationNumberRoundedIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />}
          onClick={handleCreateTicket}
        />

        <SupportActionCard
          title="Email our Team"
          subtitle="support@merudesk.com"
          gradient="linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
          color="#3B82F6"
          icon={<EmailRoundedIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />}
          onClick={handleEmailSupportClick}
          loading={loading}
          external
        />

        <SupportActionCard
          title="User Manual"
          subtitle="Read guides & documentation"
          gradient="linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
          color="#8B5CF6"
          icon={<ArticleRoundedIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />}
          onClick={downloadHelpDocument}
          external
        />

        <Typography
          sx={{
            textAlign: 'center',
            mt: 2.5,
            fontSize: '0.7rem',
            fontWeight: 500,
            color: '#9CA3AF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#10B981', boxShadow: '0 0 0 2px rgba(16,185,129,0.2)' }} />
          Typical response time: within 1 business day
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

HelpDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};